export const SUPPORTED_LOCALES = ['en', 'es-PR'] as const;

export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';

// The app ships a single Spanish catalog, localized for Puerto Rico.
export const SPANISH_LOCALE: SupportedLocale = 'es-PR';

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  'es-PR': 'Español',
};

export const LOCALE_QUERY_PARAMS = ['lang', 'locale'] as const;

export const isSupportedLocale = (value: string | null | undefined): value is SupportedLocale =>
  !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * Coerce any incoming locale code — querystring, stored device value, server
 * `preferredLocale`, or navigator language — to one of our canonical locales.
 * Any Spanish variant (`es`, `es-PR`, `es-419`, …) maps to `es-PR`; any English
 * variant maps to `en`. Returns undefined for anything unsupported. Also
 * migrates the legacy `es` value that older clients persisted.
 */
export const normalizeLocale = (value: string | null | undefined): SupportedLocale | undefined => {
  if (!value) return undefined;
  if (isSupportedLocale(value)) return value;
  const base = value.toLowerCase().split('-')[0];
  if (base === 'en') return 'en';
  if (base === 'es') return SPANISH_LOCALE;
  return undefined;
};

export const isSpanishLocale = (value: string | null | undefined): boolean => normalizeLocale(value) === SPANISH_LOCALE;

const readQueryLocale = (search: string | undefined): SupportedLocale | undefined => {
  if (!search) return undefined;
  const params = new URLSearchParams(search);
  for (const key of LOCALE_QUERY_PARAMS) {
    const candidate = normalizeLocale(params.get(key));
    if (candidate) return candidate;
  }
  return undefined;
};

/**
 * LANGUAGE-DEFAULT heuristic only — used by `detectLocale` to pick a default UI
 * language for a likely-PR device (timezone or es-PR navigator language).
 *
 * Do NOT use this for jurisdiction/location gating. Location (US vs PR) is a
 * separate concern resolved through the full waterfall (override → address →
 * IP → timezone); read it from `useLocationContext().isPuertoRico` instead, so
 * the `?location=` override and IP/address signals are respected.
 */
export const isPuertoRico = (timezone: string | undefined, language: string | undefined): boolean => {
  if (timezone === 'America/Puerto_Rico') return true;
  return language?.toLowerCase() === 'es-pr';
};

interface DetectInput {
  search?: string;
  timezone?: string;
  language?: string;
}

interface DetectResult {
  locale: SupportedLocale;
  fromQuery: boolean;
  /**
   * True when the locale came from an explicit device-language signal rather
   * than a geography guess. LocaleProvider maps this to source `device`, which
   * stops LocationLocaleSync steering the language back by location — a
   * Spanish-language device gets Spanish wherever it is.
   */
  fromLanguage: boolean;
}

/**
 * Detection order. Defaults to English.
 *   1. Querystring (?lang / ?locale) always wins.
 *   2. A Spanish device language (`es`, `es-PR`, `es-419`, `es-US`, …) — an
 *      explicit signal about the language the user reads, independent of where
 *      they are. Flagged `fromLanguage` so location can't override it.
 *   3. A likely-PR device (timezone, see `isPuertoRico`) gets Spanish as a
 *      geography-derived DEFAULT that the location waterfall may still revise.
 *
 * Note the asymmetry in 2/3: only a *Spanish* device language short-circuits.
 * An English device language falls through to the PR check, because English is
 * the out-of-the-box default on most devices — including plenty in PR — so it
 * is not evidence the user prefers English, and we'd otherwise regress PR
 * users into an English UI.
 *
 * Pure function — pass `input` for testing; otherwise inspects the runtime.
 */
export const detectLocale = (input: DetectInput = {}): DetectResult => {
  const search = input.search ?? (typeof window !== 'undefined' ? window.location.search : undefined);
  const timezone =
    input.timezone ?? (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined);
  const language = input.language ?? (typeof navigator !== 'undefined' ? navigator.language : undefined);

  const fromQuery = readQueryLocale(search);
  if (fromQuery) return { locale: fromQuery, fromQuery: true, fromLanguage: false };

  if (normalizeLocale(language) === SPANISH_LOCALE) {
    return { locale: SPANISH_LOCALE, fromQuery: false, fromLanguage: true };
  }

  if (isPuertoRico(timezone, language)) return { locale: SPANISH_LOCALE, fromQuery: false, fromLanguage: false };

  return { locale: DEFAULT_LOCALE, fromQuery: false, fromLanguage: false };
};

/**
 * Strips ?lang / ?locale from the current URL without a navigation. Call after
 * the locale has been applied so the param doesn't override the user's
 * subsequent toggle choice on refresh.
 */
export const stripLocaleQueryParam = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of LOCALE_QUERY_PARAMS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (changed) window.history.replaceState({}, '', url.toString());
};
